/**
 * Transit (gochar) confirmation — an independent timing signal alongside dasha + divisionals.
 *
 * Classical Vedic timing holds that an event ripens when the slow transiting grahas — above all
 * Jupiter and Saturn — and the running Mahadasha lord transit or aspect the houses that govern that
 * event. Where the dasha says *which* planets' period it is and the divisional says *whether* the
 * promise exists, the transit says *the sky was actually configured for it on that date*.
 *
 * Why it helps rectification specifically: the transiting positions on the event date are FIXED, but
 * which natal house each falls into is counted from the natal Ascendant — so the score changes with
 * birth time. A candidate time whose houses line up with where Jupiter/Saturn actually were scores
 * higher, giving another birth-time-sensitive vote that is independent of the dasha significators.
 *
 * Two blended sub-checks (see HOUSE_TRANSIT_SHARE / NATAL_POINT_SHARE):
 *   1. HOUSE transits (dominant): the markers transiting/aspecting the event's natal houses.
 *   2. SENSITIVE NATAL POINTS (modest share) — 08_dasha_quality_assessment.md: "Check Jupiter and
 *      Saturn transits over sensitive natal points. → Slow transits over Ascendant, Ascendant
 *      lord, Moon, Moon sign lord, Dasha lord, Antardasha lord trigger and time events." and
 *      "Dasha lord transit over key natal points → triggers the specific event to within days or
 *      weeks." A marker is "over" a point when it transits the SAME SIGN (this module is whole-sign
 *      throughout — no degree orbs are used anywhere here, so conjunction = sign identity). Points
 *      checked: the natal Ascendant, the natal Moon, and the natal position of the running
 *      Mahadasha lord — the Ascendant and Moon are birth-time-sensitive, keeping the vote
 *      discriminating.
 */

import { get_current_dasha } from "../astro/dasha";
import { dasha_lord_at_level } from "../astro/models";
import type { RawChartData } from "../astro/models";
import { _cached_event_chart } from "./scoring_common";
import { pymod, pythonRound } from "./pyutil";
import type { PlainDateTime } from "./datetime";
import {
  BAV_PLANETS,
  bhinnashtakavarga_contributors,
  kaksha_index,
  kaksha_ruler,
} from "./ashtakavarga";

// Whole-sign graha drishti as 0-indexed sign offsets ahead of the graha. Every graha aspects the
// 7th (offset 6); Jupiter also the 5th/9th, Saturn the 3rd/10th, Mars the 4th/8th.
const _ASPECT_OFFSETS: Record<string, number[]> = {
  jupiter: [6, 4, 8],
  saturn: [6, 2, 9],
  mars: [6, 3, 7],
};
const _DEFAULT_ASPECTS = [6];

export function _aspected_signs(planet: string, sign: number): Set<number> {
  return new Set((_ASPECT_OFFSETS[planet] ?? _DEFAULT_ASPECTS).map((offset) => pymod(sign + offset, 12)));
}

// How the confirmation fraction splits between the two gochar sub-checks. House transits stay
// DOMINANT (they carry the event's topical houses and are the original, benchmarked behaviour);
// the sensitive-natal-point check (08_dasha_quality_assessment.md) corroborates with a modest
// share. Exported so tests can assert the blend arithmetic instead of pinning opaque scores.
export const HOUSE_TRANSIT_SHARE = 0.75;
export const NATAL_POINT_SHARE = 0.25;

// Kaksha refinement amplitude (09_ashtakavarga_transit_rules.md, "Kaksha (Kakshya) System").
// Each sign splits into 8 kakshas of 3°45' (Saturn, Jupiter, Mars, Sun, Venus, Mercury, Moon,
// Ascendant in fixed order); a transit DELIVERS when the transiting planet sits in a kaksha whose
// ruler contributed a bindu to that sign in the transiting planet's own (natal) BAV — resolving a
// Saturn/Jupiter sign transit to ~5-7 day sub-periods, which sharpens the event-date match.
// Applied as a bounded LIFT of the blended confirmation fraction toward 1:
//     final = fraction + KAKSHA_LIFT_SHARE · kaksha_fraction · (1 − fraction)
// so a contributing kaksha strengthens the confirmation, a non-contributing kaksha contributes 0
// and merely damps the average back toward the pre-kaksha baseline, and an INDETERMINATE check
// (no marker with a computable contributor matrix — e.g. a Rahu/Ketu-only marker set or an
// incomplete natal chart) leaves the score byte-identical to the pre-kaksha formula. The doc's
// "malefic mark" grade is not representable here (the Parashari tables in ashtakavarga.ts record
// bindus only), so absence-of-bindu reads as neutral-toward-baseline, keeping the component
// never-penalising (50-100) per the module contract. Exported so tests assert the arithmetic.
// CALIBRATION (2026-07-20): amplitude benchmarked on the accuracy harness — 0.2 nudged one
// clean chart's true-sign rank down (avg 3.50 → 3.75, at the ratchet floor); 0.1 leaves every
// harness number unchanged while keeping the graded ~5-7-day sub-period signal live. Tune only
// with the harness watching.
export const KAKSHA_LIFT_SHARE = 0.1;

/**
 * Score 50-100 (confirmation-only) by how strongly the gochar markers (Jupiter, Saturn, and the
 * Mahadasha lord) confirm the event on its date:
 *   • house sub-check — marker transits (1.0) or aspects (0.5) the event's natal houses;
 *   • natal-point sub-check — marker transits the SIGN of a sensitive natal point (Ascendant,
 *     Moon, or the running Mahadasha lord's natal position; 08_dasha_quality_assessment.md).
 * The two fractions blend HOUSE_TRANSIT_SHARE : NATAL_POINT_SHARE, then the KAKSHA refinement
 * (09_ashtakavarga_transit_rules.md) lifts the blend toward 1 when the markers occupy kakshas
 * whose rulers contributed bindus in the markers' own natal BAVs (see KAKSHA_LIFT_SHARE).
 */
export function score_transit_for_event(
  natal_chart: RawChartData,
  houses: number[],
  event_dt: PlainDateTime,
  birth_dt: PlainDateTime,
  dasha_info_init: Record<string, number>,
  timezone_offset: number,
  event_lat: number,
  event_lon: number,
): [number | null, Record<string, any>] {
  const asc_sign = Math.trunc(Math.floor(natal_chart.ascendant_longitude / 30));
  const event_house_signs = new Set(houses.map((h) => pymod(asc_sign + (h - 1), 12)));
  const transit_chart = _cached_event_chart(event_dt, event_lat, event_lon, timezone_offset);

  const transit_sign = (name: string): number | null => {
    const pos = transit_chart.planets[name];
    return pos !== undefined ? Math.trunc(Math.floor(pos.longitude / 30)) : null;
  };

  const markers = ["jupiter", "saturn"];
  let maha: string | null | undefined;
  try {
    maha = dasha_lord_at_level(
      get_current_dasha(event_dt, birth_dt, dasha_info_init, timezone_offset),
      "maha",
    );
  } catch {
    maha = null;
  }
  if (maha && !markers.includes(maha)) {
    markers.push(maha);
  }

  // Sensitive NATAL point signs (08_dasha_quality_assessment.md): the natal Ascendant, the natal
  // Moon, and the natal position of the running Mahadasha lord. Whole-sign, like everything else
  // in this module — a marker "transits over" a point when it occupies the same sign.
  const natal_point_signs: Record<string, number> = { ascendant: asc_sign };
  const natal_moon = natal_chart.planets["moon"];
  if (natal_moon !== undefined) {
    natal_point_signs["moon"] = Math.trunc(Math.floor(pymod(natal_moon.longitude, 360.0) / 30));
  }
  if (maha) {
    const natal_maha = natal_chart.planets[maha];
    if (natal_maha !== undefined) {
      natal_point_signs["dasha_lord_natal"] = Math.trunc(Math.floor(pymod(natal_maha.longitude, 360.0) / 30));
    }
  }
  const sensitive_signs = new Set(Object.values(natal_point_signs));

  // Kaksha refinement (09_ashtakavarga_transit_rules.md "Kaksha System"): per marker, which
  // 3°45' kaksha of the transit sign it occupies, and whether that kaksha's ruler contributed a
  // bindu to that sign in the marker's own NATAL BAV ("check that Kaksha ruler's contribution to
  // the sign in the relevant AshtakaVarga chart"). Contributors come from the natal chart — the
  // BAV is a natal construct; the transit merely walks through it. Indeterminate (null) when the
  // marker has no BAV (Rahu/Ketu dasha lords) or the natal chart is missing AV sources.
  const contributors = bhinnashtakavarga_contributors(natal_chart);
  const av_available = (BAV_PLANETS as readonly string[]).some((p) =>
    contributors[p].some((sources) => sources.length > 0),
  );

  const house_values: number[] = [];
  const natal_values: number[] = [];
  const kaksha_values: number[] = [];
  const detail: Record<string, any> = {};
  for (const marker of markers) {
    const sign = transit_sign(marker);
    if (sign === null) {
      continue;
    }
    const occupies = event_house_signs.has(sign);
    let aspects = false;
    for (const s of _aspected_signs(marker, sign)) {
      if (event_house_signs.has(s)) {
        aspects = true;
        break;
      }
    }
    // Natal-point credit — EXCEPT the maha lord marker sitting in its OWN natal sign: every
    // graha returns to its natal sign on schedule regardless of birth time, so that self-return
    // is chance-level noise, not event confirmation. Jupiter/Saturn over the dasha lord's natal
    // point, and any marker over the Ascendant/Moon points, keep full credit.
    const natal_conjunct = Object.entries(natal_point_signs).some(
      ([point, s]) => s === sign && !(marker === maha && point === "dasha_lord_natal"),
    );
    house_values.push(occupies ? 1.0 : aspects ? 0.5 : 0.0);
    natal_values.push(natal_conjunct ? 1.0 : 0.0);
    // Kaksha sub-check: determinate only for the seven BAV planets with a computable natal BAV.
    let kaksha: number | null = null;
    let ruler: string | null = null;
    let contributing: boolean | null = null;
    const lon = pymod(transit_chart.planets[marker].longitude, 360.0);
    if (av_available && (BAV_PLANETS as readonly string[]).includes(marker)) {
      kaksha = kaksha_index(lon);
      ruler = kaksha_ruler(lon);
      contributing = contributors[marker][sign].includes(ruler);
      kaksha_values.push(contributing ? 1.0 : 0.0);
    }
    detail[marker] = {
      sign: sign,
      occupies: occupies,
      aspects: aspects,
      natal_conjunct: natal_conjunct,
      kaksha: kaksha,
      kaksha_ruler: ruler,
      kaksha_contributing: contributing,
    };
  }
  if (!house_values.length) {
    return [null, {}];
  }
  // Transit is a CONFIRMATION, not a veto: a marker not transiting the houses/points is weak
  // evidence, not disconfirmation. So map to 50 (neutral) -> 100 (fully confirmed) — it only ever
  // lifts a candidate whose houses/points align with where the slow grahas actually were, never
  // penalises. House transits dominate the blend; the sensitive-natal-point check corroborates.
  const house_fraction = house_values.reduce((a, b) => a + b, 0) / house_values.length;
  const natal_fraction = natal_values.reduce((a, b) => a + b, 0) / natal_values.length;
  const fraction = sensitive_signs.size
    ? HOUSE_TRANSIT_SHARE * house_fraction + NATAL_POINT_SHARE * natal_fraction
    : house_fraction;
  // Kaksha refinement: a bounded lift of the confirmation toward 1 when the markers sit in
  // contributing kakshas (see KAKSHA_LIFT_SHARE). kaksha_fraction = share of determinate markers
  // in a contributing kaksha; when NO marker is determinate the lift is skipped entirely and the
  // score is byte-identical to the pre-kaksha formula (never-penalising contract preserved: the
  // lift is ≥ 0 and final ≤ 1, so the component stays in 50-100).
  const kaksha_fraction = kaksha_values.length
    ? kaksha_values.reduce((a, b) => a + b, 0) / kaksha_values.length
    : null;
  const final_fraction =
    kaksha_fraction === null ? fraction : fraction + KAKSHA_LIFT_SHARE * kaksha_fraction * (1.0 - fraction);
  return [
    50.0 + 50.0 * final_fraction,
    {
      event_house_signs: [...event_house_signs].sort((a, b) => a - b),
      natal_point_signs: natal_point_signs,
      markers: detail,
      house_fraction: pythonRound(house_fraction, 2),
      natal_point_fraction: pythonRound(natal_fraction, 2),
      confirmation_fraction: pythonRound(fraction, 2),
      kaksha_fraction: kaksha_fraction === null ? null : pythonRound(kaksha_fraction, 2),
      final_fraction: pythonRound(final_fraction, 2),
    },
  ];
}
